import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveWixAcademyCertificationsAccountV1AccountCertificationsUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'api._api_base_domain_': [
      {
        srcPath: '/account-certifications',
        destPath: '',
      },
    ],
    'www._base_domain_': [
      {
        srcPath: '/_api/account-certifications',
        destPath: '',
      },
    ],
    'manage._base_domain_': [
      {
        srcPath: '/_api/account-certifications',
        destPath: '',
      },
    ],
    'www.wixapis.com': [
      {
        srcPath: '/academy/certifications/v1/account-certifications',
        destPath: '/v1/account-certifications',
      },
    ],
    '*.dev.wix-code.com': [
      {
        srcPath: '/academy/certifications/v1/account-certifications',
        destPath: '/v1/account-certifications',
      },
    ],
    _: [
      {
        srcPath: '/academy/certifications/v1/account-certifications',
        destPath: '/v1/account-certifications',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME =
  '@wix/auto_sdk_account-certifications_account-certifications';

/**
 * Retrieves a list of up to 1,000 account certifications, given the provided paging, filtering, and sorting.
 *
 * Query Account Certifications returns the full account certification records, including the underlying `userCertificationIds`, for the caller's own account only. To retrieve any account's certifications as a public, visitor-safe projection, call [List Account Certifications](https://dev.wix.com/docs/api-reference/account-level/studio-workspace/certifications/account-certification-v1/list-account-certifications) instead.
 *
 * Query Account Certifications runs with these defaults, which you can override:
 *
 * - `createdDate` is sorted in `DESC` order
 * - `cursorPaging.limit` is `50`
 *
 * You can filter and sort by `id`, `certificationType`, and `createdDate`.
 *
 * To learn about working with Query methods, see [API Query Language](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-the-wix-api-query-language), [Sorting and Paging](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-sorting-and-paging), and [Field Projection](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-field-projection).
 */
export function queryAccountCertifications(
  payload: object
): RequestOptionsFactory<any> {
  function __queryAccountCertifications({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.academy.certifications.v1.account_certification',
      method: 'POST' as any,
      methodFqn:
        'wix.academy.certifications.account.v1.AccountCertifications.QueryAccountCertifications',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixAcademyCertificationsAccountV1AccountCertificationsUrl({
        protoPath: '/v1/account-certifications/query',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'accountCertifications.createdDate' },
              { path: 'accountCertifications.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __queryAccountCertifications;
}

/**
 * Retrieves an account's certifications for public display, given the account ID.
 *
 * This is a public, visitor-safe projection that returns only each certification's type and
 * creation date. To retrieve the full account certification records, including the underlying
 * `userCertificationIds`, call [Query Account Certifications](https://dev.wix.com/docs/api-reference/account-level/studio-workspace/certifications/account-certification-v1/query-account-certifications) instead.
 */
export function listAccountCertifications(
  payload: object
): RequestOptionsFactory<any> {
  function __listAccountCertifications({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.academy.certifications.v1.account_certification',
      method: 'GET' as any,
      methodFqn:
        'wix.academy.certifications.account.v1.AccountCertifications.ListAccountCertifications',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixAcademyCertificationsAccountV1AccountCertificationsUrl({
        protoPath: '/v1/account-certifications/by-account/{accountId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [{ path: 'certifications.createdDate' }],
          },
        ]),
    };

    return metadata;
  }

  return __listAccountCertifications;
}
